<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <h3 class="mb-0">Session Attendance</h3>
          <div>
            <b-button size="sm" variant="success" class="mr-50" @click="exportAttendance">Export Excel</b-button>
            <b-button size="sm" variant="outline-secondary" :to="{ name: 'dashboard' }">Back to Events</b-button>
          </div>
        </div>

        <div v-if="canManageAttendance()" class="add-record border rounded p-1 mb-1">
          <div class="d-flex align-items-end flex-wrap">
            <div class="mr-1 mb-50" style="min-width:300px;">
              <label class="text-muted small mb-25 d-flex justify-content-between">
                <span>User</span>
                <span>
                  <a v-for="src in userSources" :key="src.key" href="#" class="mr-50" :class="form.userSource === src.key ? 'text-primary font-weight-bold' : 'text-muted'" style="font-size:0.75rem;" @click.prevent="form.userSource = src.key">{{ src.label }} ({{ src.users.length }})</a>
                </span>
              </label>
              <div class="d-flex align-items-center">
                <v-select class="flex-grow-1" :options="activeUserOptions" label="title" :reduce="u=>u.id" v-model="form.user_id" :clearable="true" :multiple="true"/>
                <a href="#" class="text-primary ml-50 text-nowrap" style="font-size:0.75rem;" @click.prevent="form.user_id = activeUserOptions.map(u=>u.id)">All</a>
              </div>
            </div>
            <div class="mr-1 mb-50" style="min-width:180px;">
              <label class="text-muted small mb-25 d-block">Status</label>
              <v-select :options="statusOptions" v-model="form.status" :reduce="v=>v"/>
            </div>
            <div class="flex-grow-1 mr-1 mb-50">
              <label class="text-muted small mb-25 d-block">Notes</label>
              <b-form-input v-model="form.notes"/>
            </div>
            <div class="mb-50">
              <b-button :disabled="!form.user_id.length || !form.status" variant="primary" @click="add">Add</b-button>
            </div>
          </div>
        </div>

        <b-table :items="records" :fields="tableFields" small bordered responsive="sm" show-empty empty-text="No attendance records">
          <template #cell(user)="{ item }">{{ (item.user && item.user.name) || '' }} {{ (item.user && item.user.surname) || '' }}</template>
          <template #cell(status)="{ item }">
            <v-select :options="statusOptions" v-model="item.status" :reduce="v=>v" @input="save(item)" :disabled="!canManageAttendance()"/>
          </template>
          <template #cell(recorded_at)="{ item }">{{ item.recorded_at ? dateFormat(item.recorded_at) : '-' }}</template>
          <template #cell(notes)="{ item }">
            <b-form-input v-model="item.notes" @change="save(item)" :disabled="!canManageAttendance()"/>
          </template>
          <template #cell(actions)="{ item }">
            <b-button v-if="canManageAttendance()" size="sm" variant="outline-danger" @click="remove(item)">Delete</b-button>
          </template>
        </b-table>
      </b-overlay>
    </b-card>

    <!-- Reminder Tracking Section -->
    <b-card class="mt-1" v-if="canManageAttendance()">
      <b-overlay :show="reminderLoading" rounded="sm" no-fade>
        <div class="d-flex align-items-center justify-content-between mb-1">
          <div>
            <h5 class="mb-0">Reminder Tracking</h5>
            <small v-if="batchReminderSentAt" class="text-muted">
              Batch reminder sent {{ dateFormat(batchReminderSentAt) }} to {{ batchParticipantsCount }} participant(s)
            </small>
            <small v-else class="text-muted">No batch reminder sent yet</small>
          </div>
          <div class="d-flex align-items-center">
            <b-form-checkbox v-model="showOnlyPending" switch class="mr-1">Show only pending</b-form-checkbox>
            <b-button size="sm" variant="outline-primary" @click="loadReminderStatus">Refresh</b-button>
          </div>
        </div>

        <!-- Summary badges -->
        <div class="d-flex flex-wrap mb-1" style="gap:0.5rem;">
          <b-badge variant="light-secondary" class="px-1 py-50">
            Total accepted: {{ reminderParticipants.length }}
          </b-badge>
          <b-badge variant="light-success" class="px-1 py-50">
            Responded: {{ respondedCount }}
          </b-badge>
          <b-badge variant="light-warning" class="px-1 py-50">
            No response: {{ noResponseCount }}
          </b-badge>
          <b-badge variant="light-danger" class="px-1 py-50">
            Not attended: {{ notAttendedCount }}
          </b-badge>
          <b-badge variant="light-info" class="px-1 py-50">
            Email not sent: {{ emailNotSentCount }}
          </b-badge>
        </div>

        <b-table
          :items="filteredParticipants"
          :fields="reminderTableFields"
          small
          bordered
          responsive="sm"
          show-empty
          empty-text="No accepted participants"
        >
          <template #cell(user)="{ item }">
            <div>{{ item.user.full_name }}</div>
            <small class="text-muted">{{ item.user.email }}</small>
          </template>

          <template #cell(attendance)="{ item }">
            <b-badge v-if="item.attendance" :variant="attendanceBadgeVariant(item.attendance.status)" class="text-capitalize">
              {{ item.attendance.status }}
              <span v-if="item.attendance.check_in_method === 'self'" title="Self-reported via email"> ✉</span>
            </b-badge>
            <b-badge v-else variant="light-secondary">No record</b-badge>
          </template>

          <template #cell(email_sent)="{ item }">
            <div v-if="item.email_sent">
              <b-badge variant="light-success">Sent ({{ item.send_count }}x)</b-badge>
              <div><small class="text-muted">{{ dateFormat(item.last_sent_at) }}</small></div>
            </div>
            <b-badge v-else variant="light-secondary">Not sent</b-badge>
          </template>

          <template #cell(response)="{ item }">
            <b-badge v-if="item.has_responded" variant="light-success">Responded</b-badge>
            <b-badge v-else-if="item.email_sent" variant="light-warning">No response</b-badge>
            <b-badge v-else variant="light-secondary">-</b-badge>
          </template>

          <template #cell(reminder_action)="{ item }">
            <b-button
              v-if="!item.has_responded"
              size="sm"
              variant="outline-primary"
              :disabled="sendingReminderId === item.user_id"
              @click="sendReminder(item)"
            >
              <span v-if="sendingReminderId === item.user_id">Sending…</span>
              <span v-else>Send Reminder</span>
            </b-button>
            <span v-else class="text-muted small">—</span>
          </template>
        </b-table>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import { BOverlay, BCard, BTable, BButton, BFormInput, BBadge, BFormCheckbox } from 'bootstrap-vue';
import vSelect from 'vue-select';
import store from '@/store';
import eventsStoreModule from './eventsStoreModule';
import { onUnmounted } from '@vue/composition-api';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import moment from 'moment';

export default {
  components: { BOverlay, BCard, BTable, BButton, BFormInput, BBadge, BFormCheckbox, vSelect, ToastificationContent },
  data() {
    return {
      loading: false,
      records: [],
      statusOptions: ['present', 'absent', 'late', 'excused'],
      audienceUsers: [],
      appliedUsers: [],
      acceptedUsers: [],
      form: { user_id: [], userSource: 'audience', status: 'present', notes: '' },
      eventAdmins: [],
      // Reminder tracking
      reminderLoading: false,
      reminderParticipants: [],
      batchReminderSentAt: null,
      batchParticipantsCount: 0,
      showOnlyPending: false,
      sendingReminderId: null,
    };
  },
  setup() {
    const MODULE = 'events';
    if (!store.hasModule(MODULE)) store.registerModule(MODULE, eventsStoreModule);
    onUnmounted(() => { if (store.hasModule(MODULE)) store.unregisterModule(MODULE); });
  },
  created() {
    this.load();
    this.loadEventAdmins();
    this.loadAudienceSummary();
    this.loadReminderStatus();
  },
  computed: {
    userSources() {
      return [
        { key: 'audience', label: 'Audience', users: this.audienceUsers },
        { key: 'applied',  label: 'Applied',  users: this.appliedUsers },
        { key: 'accepted', label: 'Accepted', users: this.acceptedUsers },
      ];
    },
    activeUserOptions() {
      const src = this.userSources.find(s => s.key === this.form.userSource);
      return src ? src.users : [];
    },
    tableFields() {
      const base = [
        { key: 'user', label: 'User' },
        { key: 'status', label: 'Status' },
        { key: 'notes', label: 'Notes' },
        { key: 'recorded_at', label: 'Recorded' },
      ];
      if (this.canManageAttendance()) base.push({ key: 'actions', label: 'Actions' });
      return base;
    },
    reminderTableFields() {
      return [
        { key: 'user', label: 'Participant' },
        { key: 'attendance', label: 'Attendance' },
        { key: 'email_sent', label: 'Email Sent' },
        { key: 'response', label: 'Response' },
        { key: 'reminder_action', label: 'Action' },
      ];
    },
    filteredParticipants() {
      if (!this.showOnlyPending) return this.reminderParticipants;
      return this.reminderParticipants.filter(p => !p.has_responded);
    },
    respondedCount() {
      return this.reminderParticipants.filter(p => p.has_responded).length;
    },
    noResponseCount() {
      return this.reminderParticipants.filter(p => !p.has_responded).length;
    },
    notAttendedCount() {
      return this.reminderParticipants.filter(p => !p.attendance || p.attendance.status === 'absent').length;
    },
    emailNotSentCount() {
      return this.reminderParticipants.filter(p => !p.email_sent).length;
    },
  },
  methods: {
    canManageAttendance() {
      return this.$CanEvent ? this.$CanEvent('manage_event_attendance', this.eventAdmins) : (this.$Can ? this.$Can('manage_event_attendance') : true);
    },
    async loadEventAdmins() {
      try {
        const sessionId = this.$route.params.sessionId;
        if (!sessionId) { this.eventAdmins = []; return; }
        const sRes = await store.dispatch('events/getSessionById', { sessionId });
        const session = (sRes && sRes.data) || {};
        let admins = [];
        const eventId = session.event_id || (session.event && session.event.id);
        if (eventId) {
          const eRes = await store.dispatch('events/getEventById', { id: eventId });
          const ev = (eRes && eRes.data) || {};
          admins = ev.admins || [];
        } else if (session.event && session.event.admins) {
          admins = session.event.admins;
        }
        this.eventAdmins = admins;
      } catch (e) {
        this.eventAdmins = [];
      }
    },
    async loadAudienceSummary() {
      try {
        const sessionId = this.$route.params.sessionId;
        if (!sessionId) return;
        const res = await store.dispatch('events/sessionAudienceSummary', { sessionId });
        const data = res.data || {};
        const toOption = u => ({ id: u.id, title: u.full_name || ((u.name || '') + (u.surname ? (' ' + u.surname) : '')).trim() || 'User #' + u.id });
        this.audienceUsers = (data.audience_users && data.audience_users.users || []).map(toOption);
        this.appliedUsers  = (data.applied_users  && data.applied_users.users  || []).map(toOption);
        this.acceptedUsers = (data.accepted_users && data.accepted_users.users || []).map(toOption);
      } catch (e) {}
    },
    async loadReminderStatus() {
      try {
        this.reminderLoading = true;
        const sessionId = this.$route.params.sessionId;
        if (!sessionId) return;
        const res = await store.dispatch('events/listAttendanceReminderStatus', { sessionId });
        const data = res.data || {};
        this.reminderParticipants   = data.participants || [];
        this.batchReminderSentAt    = data.batch_reminder_sent_at || null;
        this.batchParticipantsCount = data.batch_participants_count || 0;
      } catch (e) {
        this.$toast({ component: ToastificationContent, props: { title: 'Failed to load reminder status', icon: 'AlertTriangleIcon', variant: 'danger' } });
      } finally {
        this.reminderLoading = false;
      }
    },
    dateFormat(val) { return moment(String(val)).format('DD-MM-YYYY HH:mm'); },
    async load() {
      try {
        this.loading = true;
        const sessionId = this.$route.params.sessionId;
        const res = await store.dispatch('events/listAttendanceBySession', { sessionId });
        this.records = res.data;
      } catch (e) {
        this.$toast({ component: ToastificationContent, props: { title: 'Failed to load attendance', icon: 'AlertTriangleIcon', variant: 'danger' } });
      } finally {
        this.loading = false;
      }
    },
    async save(item) {
      try {
        await store.dispatch('events/updateAttendanceRecord', { recordId: item.id, data: { status: item.status, notes: item.notes } });
        this.$toast({ component: ToastificationContent, props: { title: 'Saved', icon: 'ThumbsUpIcon', variant: 'success' } });
      } catch (e) {
        this.$toast({ component: ToastificationContent, props: { title: 'Save failed', icon: 'AlertTriangleIcon', variant: 'danger' } });
      }
    },
    async exportAttendance() {
      try {
        const sessionId = this.$route.params.sessionId;
        const res = await store.dispatch('events/exportAttendanceBySession', { sessionId });
        this.downloadBlob(res);
      } catch (e) {
        this.$toast({ component: ToastificationContent, props: { title: 'Export failed', icon: 'AlertTriangleIcon', variant: 'danger' } });
      }
    },
    downloadBlob(res) {
      const disposition = res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition']);
      const match = disposition && disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : 'attendance.xlsx';
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    },
    async add() {
      try {
        if (!this.form.user_id.length || !this.form.status) return;
        const sessionId = this.$route.params.sessionId;
        const payload = { user_id: this.form.user_id, status: this.form.status, notes: this.form.notes || null };
        await store.dispatch('events/addAttendanceRecord', { sessionId, data: payload });
        await this.load();
        await this.loadReminderStatus();
        this.form.user_id = [];
        this.form.notes = '';
        this.$toast({ component: ToastificationContent, props: { title: 'Added', icon: 'CheckIcon', variant: 'success' } });
      } catch (e) {
        this.$toast({ component: ToastificationContent, props: { title: 'Add failed', icon: 'AlertTriangleIcon', variant: 'danger' } });
      }
    },
    async remove(item) {
      const ok = await this.$bvModal.msgBoxConfirm('Delete record?', { size: 'sm', title: 'Confirm', okVariant: 'primary', okTitle: 'Yes', cancelTitle: 'No', cancelVariant: 'outline-secondary', centered: true });
      if (!ok) return;
      await store.dispatch('events/deleteAttendanceRecord', { recordId: item.id });
      this.records = this.records.filter(r => r.id !== item.id);
      await this.loadReminderStatus();
    },
    async sendReminder(participant) {
      try {
        this.sendingReminderId = participant.user_id;
        const sessionId = this.$route.params.sessionId;
        await store.dispatch('events/sendIndividualAttendanceReminder', {
          sessionId,
          data: { user_id: participant.user_id, application_id: participant.application_id },
        });
        this.$toast({ component: ToastificationContent, props: { title: `Reminder sent to ${participant.user.full_name}`, icon: 'MailIcon', variant: 'success' } });
        await this.loadReminderStatus();
      } catch (e) {
        this.$toast({ component: ToastificationContent, props: { title: 'Failed to send reminder', icon: 'AlertTriangleIcon', variant: 'danger' } });
      } finally {
        this.sendingReminderId = null;
      }
    },
    attendanceBadgeVariant(status) {
      return { present: 'light-success', late: 'light-warning', excused: 'light-info', absent: 'light-danger' }[status] || 'light-secondary';
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
